import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { environment } from '@brighthub/environment';
import { AuthService } from '@brightside/core/auth';
import { throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import {
  StructuredLogger,
  reportApiFailure,
} from '@brighthub/structured-logging';

export interface WrapperRestApiOptionsBase {
  headers?: any;
  params?: Record<string, string>;
  body?: DocumentType | FormData | any;
  withCredentials?: boolean;
  responseType?: string;
}

export interface RestApiOptionsExtended extends WrapperRestApiOptionsBase {
  [key: string]: any;
}
@Injectable({
  providedIn: 'root',
})
export class AwsApiWrapperService {
  unauthMsg =
    'User is not authorized to access this resource with an explicit deny';

  private _authService = inject(AuthService);
  private _http = inject(HttpClient);
  private readonly _logger = inject(StructuredLogger);
  private _env = environment;

  handleError(err: HttpErrorResponse) {
    if (err.status === 403 && err.message === this.unauthMsg) {
      this.forceLogout();
    }
    return throwError(err);
  }

  get(
    apiName: string,
    path: string,
    init?: RestApiOptionsExtended,
    responseType?: string,
  ): Promise<any> {
    return this._request('get', apiName, path, init, responseType);
  }

  put(
    apiName: string,
    path: string,
    init?: RestApiOptionsExtended,
  ): Promise<any> {
    return this._request('put', apiName, path, init);
  }

  post(
    apiName: string,
    path: string,
    init?: RestApiOptionsExtended,
  ): Promise<any> {
    return this._request('post', apiName, path, init);
  }

  patch(
    apiName: string,
    path: string,
    init?: RestApiOptionsExtended,
  ): Promise<any> {
    return this._request('patch', apiName, path, init);
  }

  del(
    apiName: string,
    path: string,
    init?: RestApiOptionsExtended,
  ): Promise<any> {
    return this._request('delete', apiName, path, init);
  }

  private async _request(
    method: string,
    apiName: string,
    path: string,
    init?: RestApiOptionsExtended,
    responseType?: string,
  ): Promise<unknown> {
    const started = Date.now();
    try {
      const requestObj = await this._createRequestObj(
        apiName,
        init,
        responseType,
      );
      if (!requestObj) return false;
      const response = await this._http
        .request(method, `${this._env.apiEndpoints[apiName]}${path}`, {
          ...requestObj,
          ...(method === 'get' || method === 'delete'
            ? {}
            : { body: init?.body ?? {} }),
        })
        .pipe(catchError((err) => this.handleError(err)))
        .toPromise();
      // HTTP success can still carry a failed service result. Preserve the
      // response for its existing caller while reporting it once here.
      const status = (response as { result?: { status?: number } } | null)
        ?.result?.status;
      if (typeof status === 'number' && status >= 400) {
        reportApiFailure(this._logger, method, path, response, started);
      }
      return response;
    } catch (error) {
      reportApiFailure(this._logger, method, path, error, started);
      throw error;
    }
  }

  async _createRequestObj(
    apiName: string,
    init: RestApiOptionsExtended | undefined,
    responseType?: string,
  ): Promise<any> {
    if (!this._env.apiEndpoints[apiName]) {
      throw Error("Api endpoint doesn't exist");
    }
    const dynamicHeaders = await this._getHeaders(apiName);
    if (
      dynamicHeaders.Authorization === 'Bearer undefined' &&
      (apiName === 'api-mobile' ||
        apiName === 'api-internal' ||
        apiName === 'brightnav-api')
    ) {
      return false;
    }

    const finalOptions: WrapperRestApiOptionsBase = {
      headers: { ...dynamicHeaders },
    };
    if (init?.['queryParams']) finalOptions['params'] = init['queryParams'];
    if (init?.headers)
      finalOptions['headers'] = { ...finalOptions['headers'], ...init.headers };
    if (responseType) finalOptions['responseType'] = responseType;

    return finalOptions;
  }

  async _getHeaders(apiName: string): Promise<any> {
    let headers = {};
    switch (apiName) {
      case 'api-mobile':
      case 'api-internal':
      case 'brightnav-api':
        headers = {
          Authorization: `Bearer ${await this._authService.getAccessToken()}`,
          ...this.getStandardHeaders(),
        };
        break;
      case 'api-mobile-noauth':
        headers = { ...this.getStandardHeaders() };
        break;
    }
    return headers;
  }

  getStandardHeaders = () => {
    const headers: { [key: string]: any } = {
      Accept: 'application/json, text/plain, application/pdf, */*',
      'Content-Type': 'application/json',
      'X-BS-OS-Name': 'Desktop',
      'X-BS-Model': this.getBrowser(),
      'X-BS-Timezone': this.getTimeZone(),
      'X-BS-GmtOffset': `${new Date().getTimezoneOffset() / -60}`,
      'X-BS-DeviceId': this.getDeviceId(),
    };

    return headers;
  };

  getDeviceId = () => {
    let deviceId = window.localStorage['DEVICE_ID'];
    if (!deviceId) {
      deviceId = this.generateUUID();
      window.localStorage['DEVICE_ID'] = deviceId;
    }
    return deviceId;
  };

  getBrowser = () => {
    const userAgent = navigator.userAgent;
    const browserMapping: {
      [key: string]: {
        validMatch: string[];
        extraChecks?: (() => boolean)[];
      };
    } = {
      Opera: {
        validMatch: ['Opera', 'OPR'],
      },
      Chrome: {
        validMatch: ['Chrome'],
      },
      Safari: {
        validMatch: ['Safari'],
      },
      Firefox: {
        validMatch: ['Firefox'],
      },
      IE: {
        validMatch: ['MSIE'],
        extraChecks: [() => Object.keys(document).includes('documentMode')],
      },
    };

    return (
      Object.keys(browserMapping).filter((key: string) => {
        const checkDetails = browserMapping[key];

        if (
          checkDetails.validMatch.some((browserName) =>
            userAgent.includes(browserName),
          )
        ) {
          //If there are extra checks, make sure they all return true
          if (
            checkDetails.extraChecks &&
            !checkDetails.extraChecks.every((checkFunc) => checkFunc())
          ) {
            return false;
          }

          return true;
        }

        return false;
      })[0] || 'Unknown'
    );
  };

  getTimeZone = () => {
    const formatParts = Intl.DateTimeFormat('en', { timeZoneName: 'short' })
      .format()
      .split(' ');

    return formatParts[formatParts.length - 1];
  };

  generateUUID = () => {
    let d = new Date().getTime(); //Timestamp
    let d2 =
      (typeof performance !== 'undefined' &&
        performance.now &&
        performance.now() * 1000) ||
      0; //Time in microseconds since page-load or 0 if unsupported
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
      /[xy]/g,
      function (c) {
        let r = Math.random() * 16; //random number between 0 and 16
        if (d > 0) {
          //Use timestamp until depleted
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          // eslint-disable-next-line no-bitwise
          r = (d + r) % 16 | 0;
          d = Math.floor(d / 16);
        } else {
          //Use microseconds since page-load if supported
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          // eslint-disable-next-line no-bitwise
          r = (d2 + r) % 16 | 0;
          d2 = Math.floor(d2 / 16);
        }
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        // eslint-disable-next-line no-bitwise
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
      },
    );
  };

  forceLogout() {
    void this._authService.forceSignOut();
  }
}
